/**
 * Resolves the timestamp an inbox-style row should display for a record.
 *
 * `Ticket.updatedAt` is nullable and UAT data carries null for tickets that have
 * never been edited after creation. Passing null to `new Date()` yields the epoch,
 * which renders as 1/1/70, so callers must fall back to `createdAt` and show an
 * honest "unavailable" marker when neither field holds a parseable date. Never
 * substitute the current time: it fabricates activity that did not happen.
 *
 * Returns a normalized UTC string, or null when neither candidate is usable.
 */
export const normalizeApiTimestamp = (value?: string | null): string | null => {
  if (!value?.trim()) return null;
  const candidate = value.trim();
  // SQL DateTime values lose their UTC kind during materialisation. The API's
  // timestamp fields are UTC, including ISO datetimes sent without a zone suffix.
  const zoned = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(candidate)
    ? `${candidate}Z`
    : candidate;
  const date = new Date(zoned);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const resolveDisplayTimestamp = (
  updatedAt?: string | null,
  createdAt?: string | null,
): string | null => {
  for (const candidate of [updatedAt, createdAt]) {
    const normalized = normalizeApiTimestamp(candidate);
    if (normalized) return normalized;
  }
  return null;
};
